import React from 'react';
import { Card, Placeholder } from 'react-bootstrap';

// Pie Chart Skeleton (for OverallResultChart)
export const PieChartSkeleton = () => {
  return (
    <Card className="w-100 shadow-sm">
      <Card.Body>
        {/* Card Header */}
        <div className="mb-3">
          <Placeholder as="div" animation="glow">
            <Placeholder xs={8} className="mb-2" style={{ height: '20px' }} />
            <Placeholder xs={6} style={{ height: '14px' }} />
          </Placeholder>
        </div>
        
        {/* Pie Chart Circle */}
        <div className="d-flex justify-content-center align-items-center my-4">
          <Placeholder animation="glow">
            <Placeholder 
              style={{ 
                width: '180px', 
                height: '180px', 
                borderRadius: '50%' 
              }} 
            />
          </Placeholder>
        </div>
        
        {/* Legend */}
        <Placeholder as="div" animation="wave">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex align-items-center">
                <Placeholder 
                  style={{ 
                    width: '12px', 
                    height: '12px', 
                    borderRadius: '50%',
                    marginRight: '8px'
                  }} 
                />
                <Placeholder xs={4} style={{ height: '16px' }} />
              </div>
              <Placeholder xs={2} style={{ height: '16px' }} />
            </div>
          ))}
        </Placeholder>
      </Card.Body>
    </Card>
  );
};

// Bar Chart Skeleton (for OverallResultsByAssessmentType)
export const BarChartSkeleton = () => {
  return (
    <Card className="w-100 shadow-sm">
      <Card.Body>
        {/* Card Header */}
        <div className="mb-3">
          <Placeholder as="div" animation="glow">
            <Placeholder xs={9} className="mb-2" style={{ height: '20px' }} />
            <Placeholder xs={7} style={{ height: '14px' }} />
          </Placeholder>
        </div>
        
        {/* Chart Area */}
        <div style={{ height: '250px', position: 'relative' }}>
          {/* Y-Axis */}
          <Placeholder as="div" animation="wave" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '30px' }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} style={{ position: 'absolute', top: `${i * 25}%` }}>
                <Placeholder style={{ width: '20px', height: '10px' }} />
              </div>
            ))}
          </Placeholder>
          
          {/* Bars */}
          <div 
            className="d-flex align-items-end justify-content-around" 
            style={{ height: '100%', paddingLeft: '40px', paddingBottom: '30px', gap: '8px' }}
          >
            <Placeholder as="div" animation="glow" className="d-flex align-items-end justify-content-around w-100 h-100" style={{ gap: '8px' }}>
              {[75, 60, 85, 70, 80, 65, 90, 75].map((height, index) => (
                <Placeholder 
                  key={index} 
                  style={{ 
                    flex: 1,
                    height: `${height}%`,
                    alignSelf: 'flex-end'
                  }} 
                />
              ))}
            </Placeholder>
          </div>
          
          {/* X-Axis Labels */}
          <Placeholder 
            as="div" 
            animation="wave"
            className="d-flex justify-content-around" 
            style={{ position: 'absolute', bottom: 0, left: '40px', right: 0 }}
          >
            {[...Array(8)].map((_, i) => (
              <Placeholder key={i} style={{ width: '30px', height: '10px' }} />
            ))}
          </Placeholder>
        </div>
        
        {/* Legend */}
        <Placeholder as="div" animation="glow" className="d-flex justify-content-center gap-3 mt-3 flex-wrap">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="d-flex align-items-center gap-2">
              <Placeholder style={{ width: '10px', height: '10px', borderRadius: '50%' }} />
              <Placeholder style={{ width: '70px', height: '14px' }} />
            </div>
          ))}
        </Placeholder>
      </Card.Body>
    </Card>
  );
};

// Line Chart Skeleton (for OverallResultsProgression)
export const LineChartSkeleton = () => {
  return (
    <Card className="w-100 shadow-sm">
      <Card.Body>
        {/* Card Header */}
        <div className="mb-3">
          <Placeholder as="div" animation="glow">
            <Placeholder xs={8} className="mb-2" style={{ height: '20px' }} />
            <Placeholder xs={6} style={{ height: '14px' }} />
          </Placeholder>
        </div>
        
        {/* Chart Area */}
        <div style={{ height: '280px', position: 'relative' }}>
          {/* Y-Axis */}
          <Placeholder as="div" animation="wave" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '30px' }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} style={{ position: 'absolute', top: `${i * 20}%` }}>
                <Placeholder style={{ width: '25px', height: '10px' }} />
              </div>
            ))}
          </Placeholder>
          
          {/* Grid Lines */}
          <Placeholder as="div" animation="glow" style={{ position: 'absolute', left: '40px', right: 0, top: 0, bottom: '30px' }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} style={{ position: 'absolute', top: `${i * 20}%`, width: '100%' }}>
                <Placeholder style={{ width: '100%', height: '1px' }} />
              </div>
            ))}
          </Placeholder>
          
          {/* Line Chart Area */}
          <div style={{ position: 'absolute', left: '40px', right: 0, top: '15%', bottom: '30px' }}>
            <Placeholder animation="wave">
              <Placeholder style={{ height: '100%', width: '100%', borderRadius: '8px' }} />
            </Placeholder>
          </div>
          
          {/* X-Axis Labels */}
          <Placeholder 
            as="div" 
            animation="glow"
            className="d-flex justify-content-around" 
            style={{ position: 'absolute', bottom: 0, left: '40px', right: 0 }}
          >
            {[...Array(6)].map((_, i) => (
              <Placeholder key={i} style={{ width: '40px', height: '10px' }} />
            ))}
          </Placeholder>
        </div>
        
        {/* Legend */}
        <Placeholder as="div" animation="wave" className="d-flex justify-content-center gap-3 mt-3 flex-wrap">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="d-flex align-items-center gap-2">
              <Placeholder style={{ width: '10px', height: '10px', borderRadius: '50%' }} />
              <Placeholder style={{ width: '80px', height: '14px' }} />
            </div>
          ))}
        </Placeholder>
      </Card.Body>
    </Card>
  );
};

// Generic Dashboard Card Skeleton
export const DashboardCardSkeleton = ({ type = 'pie' }) => {
  switch (type) {
    case 'bar':
      return <BarChartSkeleton />;
    case 'line':
      return <LineChartSkeleton />;
    case 'pie':
    default:
      return <PieChartSkeleton />;
  }
};